/**
 * FSD Part 7 — Inventory. Sections follow the FSD's own structure: the eight
 * feature specifications 7.2.1–7.2.8 (INV-F01…INV-F08) in order, then the
 * part catalogue 7.1 for the catalogue requirements (INV-FR-*) that have no
 * feature specification of their own (par levels, requisitions, waste,
 * recipes, HACCP).
 *
 * Item ids are stable and must never change: favourites and recents are
 * keyed off them in localStorage. Section ids (`s-<fsd>`) are new in C17.
 *
 * F&B depletions and usage variance moved to Part 10 (POS-F08, day close) in
 * C17 — they are the POS sale's consumption of stock, not an inventory
 * feature of their own.
 *
 * Landing pages only — a detail view (a single lot, a single shipment) is
 * reached from its own list, never from the sidebar directly.
 */
import type { NavGroup } from '../types'
import { INVENTORY_PERMISSIONS } from '@/modules/inventory/permissions'

export const P07_INVENTORY_GROUPS: NavGroup[] = [
  {
    id: 's-7.2.1',
    kind: 'feature',
    fsd: '7.2.1',
    code: 'INV-F01',
    labelKey: 'nav.fsd.s7_2_1.short',
    fullTitleKey: 'nav.fsd.s7_2_1.full',
    items: [
      // Warehouses reads on `inventory.warehouse.read` — narrower than the
      // `inventory.stock.read` the movement screens use, and exactly what its
      // route declares.
      {
        id: 'inv-warehouses',
        labelKey: 'inventory.routeTitles.warehouses',
        to: '/inventory/warehouses',
        icon: 'building',
        permission: INVENTORY_PERMISSIONS.warehouseRead,
      },
      {
        id: 'inv-warehouse-layout',
        labelKey: 'inventory.routeTitles.warehouseLayout',
        to: '/inventory/warehouse-layout',
        icon: 'sitemap',
        permission: INVENTORY_PERMISSIONS.zoneRead,
      },
    ],
  },
  {
    id: 's-7.2.2',
    kind: 'feature',
    fsd: '7.2.2',
    code: 'INV-F02',
    labelKey: 'nav.fsd.s7_2_2.short',
    fullTitleKey: 'nav.fsd.s7_2_2.full',
    items: [
      // Items is specified, not extraneous: INV-F02 states "The item master
      // defines the tracking policy" (FSD text, line 8317), so the item master
      // is this feature's own screen.
      {
        id: 'inv-items',
        labelKey: 'inventory.routeTitles.items',
        to: '/inventory/items',
        icon: 'building',
        permission: INVENTORY_PERMISSIONS.itemRead,
        descriptionKey: 'nav.items.invItems.description',
      },
      {
        id: 'inv-lots',
        labelKey: 'inventory.routeTitles.lots',
        to: '/inventory/lots',
        icon: 'clock',
        permission: INVENTORY_PERMISSIONS.lotRead,
      },
      {
        id: 'inv-recalls',
        labelKey: 'inventory.routeTitles.recalls',
        to: '/inventory/recalls',
        icon: 'alert',
        permission: INVENTORY_PERMISSIONS.recallRead,
      },
      {
        id: 'inventory-serials',
        labelKey: 'inventory.routeTitles.serials',
        to: '/inventory/serials',
        icon: 'key',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
    ],
  },
  {
    id: 's-7.2.3',
    kind: 'feature',
    fsd: '7.2.3',
    code: 'INV-F03',
    labelKey: 'nav.fsd.s7_2_3.short',
    fullTitleKey: 'nav.fsd.s7_2_3.full',
    items: [
      {
        id: 'inv-stock-levels',
        labelKey: 'inventory.routeTitles.stockLevels',
        to: '/inventory/stock-levels',
        icon: 'dashboard',
        permission: INVENTORY_PERMISSIONS.stockRead,
        descriptionKey: 'nav.items.invStockLevels.description',
      },
      {
        id: 'inv-ledger',
        labelKey: 'inventory.routeTitles.stockLedger',
        to: '/inventory/stock-ledger',
        icon: 'history',
        permission: INVENTORY_PERMISSIONS.stockRead,
        descriptionKey: 'nav.items.invLedger.description',
      },
      {
        id: 'inv-transfers',
        labelKey: 'inventory.routeTitles.transfers',
        to: '/inventory/transfers',
        icon: 'refresh',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
    ],
  },
  {
    id: 's-7.2.4',
    kind: 'feature',
    fsd: '7.2.4',
    code: 'INV-F04',
    labelKey: 'nav.fsd.s7_2_4.short',
    fullTitleKey: 'nav.fsd.s7_2_4.full',
    items: [
      {
        id: 'inv-reservations',
        labelKey: 'inventory.routeTitles.reservations',
        to: '/inventory/reservations',
        icon: 'shieldCheck',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
      {
        id: 'inventory-shortages',
        labelKey: 'inventory.routeTitles.shortages',
        to: '/inventory/shortages',
        icon: 'alert',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
      {
        id: 'inventory-substitutes',
        labelKey: 'inventory.routeTitles.substitutes',
        to: '/inventory/substitutes',
        icon: 'refresh',
        permission: INVENTORY_PERMISSIONS.itemRead,
      },
    ],
  },
  {
    id: 's-7.2.5',
    kind: 'feature',
    fsd: '7.2.5',
    code: 'INV-F05',
    labelKey: 'nav.fsd.s7_2_5.short',
    fullTitleKey: 'nav.fsd.s7_2_5.full',
    items: [
      {
        id: 'inv-cycle-counts',
        labelKey: 'inventory.routeTitles.cycleCounts',
        to: '/inventory/cycle-counts',
        icon: 'check',
        permission: INVENTORY_PERMISSIONS.stockRead,
        descriptionKey: 'nav.items.invCycleCounts.description',
      },
      {
        id: 'inv-adjustments',
        labelKey: 'inventory.routeTitles.adjustments',
        to: '/inventory/adjustments',
        icon: 'scale',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
      // Reason codes read on `inventory.item.read`, exactly what the route
      // declares. Adjustments and reason codes link to each other, but a link
      // between two screens is only a door if something already open leads to
      // one of them — so both keep their own sidebar entry.
      {
        id: 'inv-reason-codes',
        labelKey: 'inventory.routeTitles.reasonCodes',
        to: '/inventory/reason-codes',
        icon: 'settings',
        permission: INVENTORY_PERMISSIONS.itemRead,
      },
      {
        id: 'inventory-shrinkage',
        labelKey: 'inventory.routeTitles.shrinkage',
        to: '/inventory/reports/shrinkage',
        icon: 'history',
        permission: INVENTORY_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-7.2.6',
    kind: 'feature',
    fsd: '7.2.6',
    code: 'INV-F06',
    labelKey: 'nav.fsd.s7_2_6.short',
    fullTitleKey: 'nav.fsd.s7_2_6.full',
    items: [
      {
        id: 'inv-valuation',
        labelKey: 'inventory.routeTitles.valuation',
        to: '/inventory/reports/valuation',
        icon: 'download',
        permission: INVENTORY_PERMISSIONS.reportRead,
        descriptionKey: 'nav.items.invValuation.description',
      },
      {
        id: 'inv-recon',
        labelKey: 'inventory.routeTitles.recon',
        to: '/inventory/recon',
        icon: 'check',
        permission: INVENTORY_PERMISSIONS.reconRead,
      },
      {
        id: 'inv-revaluations',
        labelKey: 'inventory.routeTitles.revaluations',
        to: '/inventory/revaluations',
        icon: 'history',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
      {
        id: 'inventory-settings',
        labelKey: 'inventory.routeTitles.settings',
        to: '/inventory/settings',
        icon: 'settings',
        permission: INVENTORY_PERMISSIONS.settingsRead,
      },
    ],
  },
  {
    id: 's-7.2.7',
    kind: 'feature',
    fsd: '7.2.7',
    code: 'INV-F07',
    labelKey: 'nav.fsd.s7_2_7.short',
    fullTitleKey: 'nav.fsd.s7_2_7.full',
    items: [
      {
        id: 'inv-replenishment',
        labelKey: 'inventory.routeTitles.replenishment',
        to: '/inventory/replenishment',
        icon: 'refresh',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
      {
        id: 'inv-reorder-suggestions',
        labelKey: 'inventory.routeTitles.reorderSuggestions',
        to: '/inventory/reorder-suggestions',
        icon: 'alert',
        permission: INVENTORY_PERMISSIONS.stockRead,
      },
      {
        id: 'inventory-classification',
        labelKey: 'inventory.routeTitles.classification',
        to: '/inventory/classification',
        icon: 'sitemap',
        permission: INVENTORY_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-7.2.8',
    kind: 'feature',
    fsd: '7.2.8',
    code: 'INV-F08',
    labelKey: 'nav.fsd.s7_2_8.short',
    fullTitleKey: 'nav.fsd.s7_2_8.full',
    items: [
      {
        id: 'inv-landed-cost',
        labelKey: 'inventory.routeTitles.shipments',
        to: '/inventory/landed-cost',
        icon: 'globe',
        permission: INVENTORY_PERMISSIONS.landedCostRead,
      },
      {
        id: 'inventory-landed-cost-setup',
        labelKey: 'inventory.routeTitles.landedCostSetup',
        to: '/inventory/landed-cost/setup',
        icon: 'settings',
        permission: INVENTORY_PERMISSIONS.landedCostRead,
      },
      {
        id: 'inventory-landed-cost-aging',
        labelKey: 'inventory.routeTitles.landedCostAging',
        to: '/inventory/reports/landed-cost-aging',
        icon: 'clock',
        permission: INVENTORY_PERMISSIONS.reportRead,
      },
    ],
  },
  // Catalogue 7.1 — requirements specified only in the part catalogue, each
  // item carrying its own requirement id.
  {
    id: 's-7.1',
    kind: 'catalogue',
    fsd: '7.1',
    labelKey: 'nav.fsd.s7_1.short',
    fullTitleKey: 'nav.fsd.s7_1.full',
    items: [
      {
        id: 'inv-par-levels',
        labelKey: 'inventory.routeTitles.parLevels',
        to: '/inventory/par-levels',
        icon: 'scale',
        permission: INVENTORY_PERMISSIONS.parRead,
        code: 'INV-FR-017',
      },
      {
        id: 'inv-requisitions',
        labelKey: 'inventory.routeTitles.requisitions',
        to: '/inventory/requisitions',
        icon: 'mail',
        permission: INVENTORY_PERMISSIONS.requisitionRead,
        code: 'INV-FR-018',
      },
      {
        id: 'inv-waste',
        labelKey: 'inventory.routeTitles.waste',
        to: '/inventory/waste',
        icon: 'ban',
        permission: INVENTORY_PERMISSIONS.stockRead,
        code: 'INV-FR-019',
      },
      {
        id: 'inv-recipes',
        labelKey: 'inventory.routeTitles.recipes',
        to: '/inventory/recipes',
        icon: 'star',
        permission: INVENTORY_PERMISSIONS.recipeRead,
        code: 'INV-FR-020',
      },
      {
        id: 'inv-haccp',
        labelKey: 'inventory.routeTitles.haccp',
        to: '/inventory/haccp',
        icon: 'shield',
        permission: INVENTORY_PERMISSIONS.haccpRead,
        code: 'INV-FR-025',
      },
    ],
  },
]
